import { useState } from "react";
import { motion } from "framer-motion";
import {
  Send, MapPin, Mail,  Phone,
  ChevronRight, ChevronDown, Navigation, Smartphone,
  CheckCircle, Loader2, AlertCircle
} from "lucide-react";
import endpoints from "../config/endpoints.json";
import options from "../data/quotationOptions.json";
import { GreenIconTile, TILE_ICON_SIZE } from "./GreenIconTile";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";
import PrivacyConsentCheckbox from "./PrivacyConsentCheckbox";
import ConsentAwareEmbed from "./ConsentAwareEmbed";

const ContactSection = () => {
  /** Principal: CRM match 1 a 1 / turno vendedora. */
  const crmGasUrl = endpoints.crmLeadForm?.gasUrl || "";
  /** Respaldo legado (sin asignación comercial). */
  const legacyGasUrl = endpoints.contactForm?.gasUrl || "";
  const {
    lat,
    lng,
    googleMapsEmbedUrl,
    navigationBaseUrl,
    addressFull,
    addressShort,
    email,
    correo,
    telefono,
    phoneDisplay,
    whatsapp,
    cellphoneDisplay,
  } = endpoints.location;

  const navUrl = `${navigationBaseUrl}${lat},${lng}`;

  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    telefono: "",
    email: "",
    motivo: "",
    mensaje: "",
    website_url: "",
  });

  const [status, setStatus] = useState("idle");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name in formData) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      setStatus("error");
      return;
    }
    if (!formData.email.includes("@") || formData.motivo === "") {
      setStatus("error");
      return;
    }
    setStatus("loading");
    try {
      if (!crmGasUrl) {
        console.warn(
          "[Consulta] crmLeadForm.gasUrl vacío: no habrá match 1 a 1 ni email a vendedora."
        );
      }
      const submitLead = httpsCallable(functions, "submitWebsiteLead");
      await submitLead({
        ...formData,
        tipoFormulario: "CONSULTA_GENERAL",
        privacyConsent: true,
        privacyConsentAt: new Date().toISOString(),
        forwardGasUrl: crmGasUrl,
        legacyGasUrl: legacyGasUrl,
      });
      setStatus("success");
      setPrivacyAccepted(false);
      setFormData({ nombre: "", apellido: "", email: "", telefono: "", motivo: "", mensaje: "", website_url: "" });
      setTimeout(() => setStatus("idle"), 6000);
    } catch (error) {
      console.error("[ContactSection] Error en envío:", error);
      // Si el CRM falla (p. ej. sin vendedoras), intentar solo el GAS legado a info@
      // (respuesta legible; no usar no-cors porque finge éxito).
      try {
        if (!legacyGasUrl) throw error;
        const response = await fetch(legacyGasUrl, {
          method: "POST",
          cache: "no-cache",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify({ ...formData, tipoFormulario: "CONSULTA_GENERAL" }),
          redirect: "follow",
        });
        const data = await response.json();
        if (data.result !== "success") {
          throw new Error(data.message || "Legado no aceptó la consulta");
        }
        setStatus("success");
        setPrivacyAccepted(false);
        setFormData({ nombre: "", apellido: "", email: "", telefono: "", motivo: "", mensaje: "", website_url: "" });
        setTimeout(() => setStatus("idle"), 6000);
      } catch (fallbackErr) {
        console.error("[ContactSection] Fallback legado falló:", fallbackErr);
        setStatus("error");
        setTimeout(() => setStatus("idle"), 5000);
      }
    }
  };

  return (
    <section id="contacto" className="py-24 bg-white overflow-hidden font-body">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-16 items-start">

          {/* FORMULARIO */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 bg-[#f0f4f2] px-4 py-2 rounded-lg mb-6">
              <span className="text-brand-primary font-bold text-xs uppercase tracking-widest flex items-center gap-1">
                Contáctanos <ChevronRight size={14} className="text-brand-accent" />
                <ChevronRight size={14} className="text-brand-accent/60" />
                <ChevronRight size={14} className="text-brand-accent/30" />
              </span>
            </div>

            <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-dark leading-tight mb-8">
              Envíanos tu consulta y nos <span className="text-brand-primary">pondremos en contacto</span>
            </h2>

            <div className="h-1 w-20 bg-brand-accent mb-12 rounded-full" />

            {status === "success" ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-brand-soft p-10 rounded-[40px] text-center border border-brand-primary/20 shadow-sm"
              >
                <CheckCircle className="mx-auto text-brand-primary mb-4" size={48} />
                <h3 className="text-brand-dark font-bold text-xl mb-2">¡Mensaje Recibido!</h3>
                <p className="text-brand-dark/70">Gracias por escribirnos. Nuestro equipo técnico responderá a la brevedad posible.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    required
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    type="text"
                    placeholder="Nombre"
                    className="bg-[#f5f7f6] border-none rounded-xl p-5 focus:ring-2 focus:ring-brand-primary transition-all outline-none"
                  />
                  <input
                    name="apellido"
                    value={formData.apellido}
                    onChange={handleChange}
                    type="text"
                    placeholder="Apellido"
                    className="bg-[#f5f7f6] border-none rounded-xl p-5 focus:ring-2 focus:ring-brand-primary transition-all outline-none"
                  />
                  <input
                    required
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    type="text"
                    inputMode="numeric"
                    placeholder="Teléfono (Solo números)"
                    className="bg-[#f5f7f6] border-none rounded-xl p-5 outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                  <input
                    required
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    type="email"
                    placeholder="Correo electrónico"
                    className="bg-[#f5f7f6] border-none rounded-xl p-5 focus:ring-2 focus:ring-brand-primary transition-all outline-none"
                  />
                </div>

                <div className="relative">
                  <select
                    required
                    name="motivo"
                    value={formData.motivo}
                    onChange={handleChange}
                    className="w-full bg-[#f5f7f6] border-none rounded-xl p-5 text-gray-700 focus:ring-2 focus:ring-brand-primary transition-all outline-none appearance-none font-medium leading-tight cursor-pointer"
                  >
                    <option value="" disabled>Seleccione el motivo de su consulta</option>
                    {options.motivoconsulta.map((item, index) => (
                      <option key={index} value={item} className="text-brand-dark py-2">
                        {item}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                    <ChevronDown size={20} />
                  </div>
                </div>

                <textarea
                  required
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  placeholder="Escribe detalles adicionales de tu consulta..."
                  rows="4"
                  className="w-full bg-[#f5f7f6] border-none rounded-xl p-5 focus:ring-2 focus:ring-brand-primary transition-all outline-none resize-none"
                />

                <PrivacyConsentCheckbox
                  id="contact-privacy"
                  checked={privacyAccepted}
                  onChange={setPrivacyAccepted}
                  className="mt-4"
                />

                <div className="flex flex-col md:flex-row items-center justify-between gap-6 mt-6">
                  <button
                    type="submit"
                    disabled={status === "loading" || !privacyAccepted}
                    className="bg-[#1e5a3a] hover:bg-[#0d2a1b] text-white px-10 py-5 rounded-lg font-bold flex items-center gap-3 transition-all shadow-lg active:scale-95 w-full md:w-auto justify-center disabled:opacity-70"
                  >
                    {status === "loading" ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      <>Enviar Consulta <Send size={18} /></>
                    )}
                  </button>
                </div>

                {status === "error" && (
                  <div className="flex items-center gap-2 text-red-600 mt-2 font-bold text-sm bg-red-50 p-3 rounded-lg">
                    <AlertCircle size={16} /> Verifique que todos los campos y el motivo estén seleccionados.
                  </div>
                )}

                <div className="hidden" aria-hidden="true">
                  <input
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>
              </form>
            )}
          </motion.div>

          {/* MAPA Y DATOS DE CONTACTO */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-6 flex flex-col h-full"
          >
            <div className="relative group flex-grow">
              <a
                href={navUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-6 right-6 z-30 bg-white/90 backdrop-blur-md text-brand-dark px-4 py-2 rounded-full shadow-xl font-bold text-xs flex items-center gap-2 hover:bg-brand-accent hover:text-brand-dark transition-all scale-90 group-hover:scale-100"
              >
                <Navigation size={14} /> ABRIR EN GPS
              </a>

              <div className="absolute -inset-4 bg-brand-primary/5 rounded-[50px] blur-2xl group-hover:bg-brand-primary/10 transition-colors" />

              <div className="relative rounded-[40px] overflow-hidden shadow-2xl border-8 border-white h-[450px]">
                <ConsentAwareEmbed
                  title="Mapa de ubicación"
                  description="Google Maps es un servicio de terceros. Actívelo solo si acepta cookies de contenido externo."
                  className="h-full rounded-none border-0"
                  minHeight="100%"
                >
                  <iframe
                    src={googleMapsEmbedUrl}
                    className="w-full h-full border-0 grayscale-[0.2] contrast-[1.1] pointer-events-auto"
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Ubicación AGDYSA"
                  />
                </ConsentAwareEmbed>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">

                {/* Card 1: Dirección */}
                <a
                  href={navUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group h-full"
                >
                  <motion.div
                    whileHover={{ y: -5 }}
                    className="bg-brand-dark p-6 rounded-[32px] text-white relative overflow-hidden shadow-xl border border-white/10 transition-colors h-full"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent opacity-10 blur-3xl" />
                    <div className="flex items-start gap-4 relative z-10">
                      <GreenIconTile rounded="rounded-2xl">
                        <MapPin size={TILE_ICON_SIZE} />
                      </GreenIconTile>
                      <div>
                        <p className="text-brand-accent font-bold uppercase tracking-widest text-[12px] mb-2">
                          {addressShort}
                        </p>
                        <span className="bg-brand-accent/20 px-2 py-0.5 rounded text-[15px] text-white font-bold">NAVEGAR</span>
                        <p className="px-2 py-0.5 text-[20px] text-white mt-1">
                          {addressFull}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </a>

                {/* Card 2: Correo y Teléfono */}
                <motion.div
                  whileHover={{ y: -5 }}
                  className="bg-brand-dark p-6 rounded-[32px] text-white relative overflow-hidden shadow-xl border border-white/10 transition-colors h-full"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent opacity-10 blur-3xl" />
                  <div className="relative z-10 flex flex-col justify-center gap-5 h-full">
                    <a
                      href={email}
                      className="group flex items-center gap-4 min-w-0"
                    >
                      <GreenIconTile rounded="rounded-2xl">
                        <Mail size={TILE_ICON_SIZE} />
                      </GreenIconTile>
                      <span className="text-white group-hover:text-brand-accent transition-colors text-base font-medium truncate">
                        {correo}
                      </span>
                    </a>
                    <a
                      href={whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-4"
                    >
                      <GreenIconTile rounded="rounded-2xl">
                        <Smartphone size={TILE_ICON_SIZE} />
                      </GreenIconTile>
                      <span className="text-white group-hover:text-brand-accent transition-colors text-base font-medium">
                        {cellphoneDisplay}
                      </span>
                    </a>
                    <a
                      href={telefono}
                      className="group flex items-center gap-4"
                    >
                      <GreenIconTile rounded="rounded-2xl">
                        <Phone size={TILE_ICON_SIZE} />
                      </GreenIconTile>
                      <span className="text-white group-hover:text-brand-accent transition-colors text-base font-medium">
                        {phoneDisplay}
                      </span>
                    </a>
                  </div>
                </motion.div>

              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default ContactSection;
